import api from './api';

export type PoolAlertDeviceType = 'siren' | 'buzzer';

export type AlertDeviceLinkMode = 'client' | 'tcp_server_a';

export type PoolAlertDeviceProtocol = 'tcp' | 'http' | 'tcp_server';

export type PoolAlertDeviceRecord = {
  id: number;
  pool_id: number;
  device_type: PoolAlertDeviceType;
  label: string | null;
  host: string;
  port: number | null;
  location_hint: string | null;
  protocol: PoolAlertDeviceProtocol;
  template_key: string | null;
  tcp_append_newline: boolean;
  blink_enabled: boolean;
  tcp_command_start_danger: string | null;
  tcp_command_start_warning: string | null;
  tcp_command_stop: string | null;
  http_method: 'GET' | 'POST';
  http_start_path_danger: string | null;
  http_start_path_warning: string | null;
  http_stop_path: string | null;
  socket_timeout_sec: number | null;
  is_active: boolean;
  sort_order: number;
  display_label: string;
};

export type PoolAlertDeviceListItem = {
  id: number;
  index: number;
  label: string;
  address: string;
};

export type AlertDeviceTemplate = {
  key: string;
  name: string;
  device_type: PoolAlertDeviceType;
  protocol: PoolAlertDeviceProtocol;
  tcp_append_newline: boolean;
  tcp_command_start_danger: string | null;
  tcp_command_start_warning: string | null;
  tcp_command_stop: string | null;
  http_method: 'GET' | 'POST';
  http_start_path_danger: string | null;
  http_start_path_warning: string | null;
  http_stop_path: string | null;
  socket_timeout_sec: number | null;
  default_port: number | null;
  hint?: string | null;
};

export const ALERT_DEVICE_TARGET_ALL = 'all';

export type PoolAlertDeviceActionResponse = {
  pool_id: number;
  device_type: string;
  action: string;
  target?: string;
  success: boolean;
  results: Array<{ device_id: number; label: string; ok: boolean; error: string | null }>;
  active_device_ids?: number[];
  gateway_command_id?: string | null;
};

function toListItem(d: PoolAlertDeviceRecord, index: number): PoolAlertDeviceListItem {
  const name = (d.label || '').trim();
  const addr = d.port ? `${d.host}:${d.port}` : d.host;
  const listLabel = name ? `${name} (${addr})` : d.display_label || addr;
  return {
    id: d.id,
    index,
    label: listLabel,
    address: addr,
  };
}

/** 시설 관리 → 경광등/부저 등록 탭 목록: 표시 이름(label) 가나다순 */
export function comparePoolAlertDevicesByDisplayName(
  a: PoolAlertDeviceRecord,
  b: PoolAlertDeviceRecord
): number {
  const la = (a.label || '').trim();
  const lb = (b.label || '').trim();
  if (la && lb) {
    const byLabel = la.localeCompare(lb, 'ko');
    if (byLabel !== 0) return byLabel;
  } else if (la && !lb) return -1;
  else if (!la && lb) return 1;
  const byHost = (a.host || '').localeCompare(b.host || '', 'ko');
  if (byHost !== 0) return byHost;
  return a.id - b.id;
}

export async function fetchAlertDeviceTemplates(
  deviceType?: PoolAlertDeviceType
): Promise<AlertDeviceTemplate[]> {
  const res = await api.get<{ templates: AlertDeviceTemplate[] }>(
    `/api/pools/alert-device-templates${deviceType ? `?device_type=${deviceType}` : ''}`
  );
  return res.templates ?? [];
}

export async function fetchPoolAlertDevices(
  poolId: number,
  deviceType: PoolAlertDeviceType
): Promise<PoolAlertDeviceListItem[]> {
  const res = await api.get<{ devices: PoolAlertDeviceRecord[] }>(
    `/api/pools/${poolId}/alert-devices?device_type=${deviceType}`
  );
  return (res.devices ?? []).map(toListItem);
}

export async function fetchPoolAlertDevicesAdmin(
  poolId: number,
  deviceType: PoolAlertDeviceType,
  linkMode?: AlertDeviceLinkMode
): Promise<PoolAlertDeviceRecord[]> {
  const qs = new URLSearchParams({
    device_type: deviceType,
    include_inactive: 'true',
  });
  if (linkMode) qs.set('link_mode', linkMode);
  // 저장 직후 blink 등 필드가 캐시된 옛 목록으로 덮이지 않게
  const res = await api.get<{ devices: PoolAlertDeviceRecord[] }>(
    `/api/pools/${poolId}/alert-devices?${qs.toString()}`,
    undefined,
    { useCache: false }
  );
  return [...(res.devices ?? [])].sort(comparePoolAlertDevicesByDisplayName);
}

export async function createPoolAlertDevice(
  poolId: number,
  body: Partial<PoolAlertDeviceRecord> & { device_type: PoolAlertDeviceType; host: string }
): Promise<PoolAlertDeviceRecord> {
  const row = await api.post<PoolAlertDeviceRecord>(`/api/pools/${poolId}/alert-devices`, body);
  api.invalidateGetCache(`/api/pools/${poolId}/alert-devices`);
  return row;
}

export async function updatePoolAlertDevice(
  poolId: number,
  deviceId: number,
  body: Partial<PoolAlertDeviceRecord>
): Promise<PoolAlertDeviceRecord> {
  const row = await api.put<PoolAlertDeviceRecord>(
    `/api/pools/${poolId}/alert-devices/${deviceId}`,
    body
  );
  api.invalidateGetCache(`/api/pools/${poolId}/alert-devices`);
  return row;
}

export async function deletePoolAlertDevice(poolId: number, deviceId: number): Promise<void> {
  await api.delete(`/api/pools/${poolId}/alert-devices/${deviceId}`);
  api.invalidateGetCache(`/api/pools/${poolId}/alert-devices`);
}

function controlBase(poolId: number, deviceType: PoolAlertDeviceType): string {
  return deviceType === 'buzzer'
    ? `/api/pools/${poolId}/buzzers`
    : `/api/pools/${poolId}/sirens`;
}

export async function startPoolAlertDevices(params: {
  poolId: number;
  deviceType: PoolAlertDeviceType;
  target: string;
  alertLevel: 'warning' | 'danger';
}): Promise<PoolAlertDeviceActionResponse> {
  return api.post(`${controlBase(params.poolId, params.deviceType)}/start`, {
    target: params.target,
    alert_level: params.alertLevel,
  });
}

export async function stopPoolAlertDevices(params: {
  poolId: number;
  deviceType: PoolAlertDeviceType;
  target: string;
}): Promise<PoolAlertDeviceActionResponse> {
  return api.post(`${controlBase(params.poolId, params.deviceType)}/stop`, {
    target: params.target,
  });
}

export async function stopActivePoolAlertDevices(
  poolId: number,
  deviceType: PoolAlertDeviceType
): Promise<PoolAlertDeviceActionResponse> {
  return api.post(`${controlBase(poolId, deviceType)}/stop-active`);
}

/** 해당 유형 전체 강제 중지 (이벤트/수동 울림 해제) */
export async function forceStopPoolAlertDevices(
  poolId: number,
  deviceType: PoolAlertDeviceType,
  options?: { silentError?: boolean }
): Promise<PoolAlertDeviceActionResponse> {
  return api.post(
    `${controlBase(poolId, deviceType)}/force-stop`,
    undefined,
    { silentError: options?.silentError === true }
  );
}

export type PoolAlertDeviceTestResponse = PoolAlertDeviceActionResponse & {
  duration_sec?: number;
  alert_level?: string;
  auto_stop_scheduled?: boolean;
};

export async function testPoolAlertDevice(params: {
  poolId: number;
  deviceId?: number | null;
  deviceType: PoolAlertDeviceType;
  durationSec: number;
  alertLevel?: 'warning' | 'danger';
  inlineConfig?: Partial<PoolAlertDeviceRecord> & { device_type: PoolAlertDeviceType; host: string };
}): Promise<PoolAlertDeviceTestResponse> {
  const body: Record<string, unknown> = {
    duration_sec: params.durationSec,
    alert_level: params.alertLevel ?? 'warning',
  };
  if (params.deviceId != null) {
    return api.post(
      `/api/pools/${params.poolId}/alert-devices/${params.deviceId}/test`,
      body
    );
  }
  if (!params.inlineConfig?.host?.trim()) {
    throw new Error('호스트(IP)를 입력');
  }
  Object.assign(body, params.inlineConfig);
  return api.post(`/api/pools/${params.poolId}/alert-devices/test`, body);
}

function formatTestResult(res: PoolAlertDeviceTestResponse): string {
  const ok = res.results?.filter((r) => r.ok).length ?? 0;
  const total = res.results?.length ?? 0;
  const sec = res.duration_sec ?? 0;
  if (res.success) {
    return sec > 0
      ? `테스트 울림 시작 (${sec}초 후 자동 중지)`
      : '테스트 울림 전송';
  }
  const err = res.results?.find((r) => r.error)?.error;
  return err || `테스트 실패 (${ok}/${total})`;
}

export { formatTestResult as formatPoolAlertDeviceTestResult };
